/**
 * em-family-configurator.js — P2a Ring 1: the PDP presents its product FAMILY
 * (same custom.product_core_name) as one product with option rows — LENGTH /
 * POCKETS / COLOUR — under the size row. Tapping an option silently SPA-swaps
 * to the sibling product (_v2SwapToHandle); the shopper's size follows her
 * (size persistence shipped earlier), so it reads as variant selection.
 *
 * Locked rules (28 Jul 2026):
 *  D1 hide-mode — once the shopper has PICKED a size (trusted pick, not the
 *     auto-select), options without that size in sellable stock are HIDDEN.
 *     Before a pick, the full family matrix renders pressable. One exception:
 *     the combo she is currently ON stays visible-struck when dead (the page
 *     cannot hide its own state).
 *  D2 rescue line: "Size M is ready to ship in full length".
 *  D5 no auto-swap — a swap only ever happens on her tap.
 *  In-stock = catalog qty > 0 (ring 1 sells what ships now; pre-order stays
 *  ring 3 / existing PDP behaviour). Bucket rules: family only — no lifestage
 *  jumps on the PDP.
 *
 * Data: window.__emPackCatalog (pack-catalog.liquid, on every page).
 * Scope: EVERY leggings/shorts family with 2+ members (UAT 28 Jul: no
 * allowlist — "options don't always display" on shorts). Tops carry no axes
 * and never render rows. Shorts rules (locked): bike shorts = shorts;
 * long shorts is the only shorts length variation.
 */
(function () {
  'use strict';

  // LENGTH is one spectrum across the whole bottoms RANGE (28 Jul): a shopper
  // on Maternity Shorts also sees 3/4 / 7/8 / full length — tapping one swaps
  // to the leggings sibling. Short-to-long order.
  var AXES = [
    { key: 'length', label: 'length', order: ['shorts', 'long shorts', '3/4', '7/8', 'full length'] },
    { key: 'feature', label: 'pockets', order: ['with pockets', 'no pockets'] },
    { key: 'colour', label: 'colour', order: ['black'] } // black first, rest alpha
  ];
  var SWATCH = {
    black: '#26262a', olive: '#6b7250', twilight: '#3d4a63', spearmint: '#9fc6b8',
    caramel: '#b07f4f', rose: '#d9a0a0', plum: '#7d5670', nude: '#d9c2ad',
    berry: '#a04a5e', lilac: '#b9a6cc', white: '#f2f2f2', charcoal: '#4a4a4e'
  };
  var HOST_ID = 'em-family-config';

  function catalog() { return window.__emPackCatalog || null; }
  // Current product handle. pdpMeta first: on the HOME SPA the path is "/"
  // until the first swap, so the URL alone goes blind exactly where the SPA
  // matters most. pdpMeta is re-parsed by the swap code, so it tracks both
  // surfaces from the same source of truth.
  function currentHandle() {
    if (window.pdpMeta && window.pdpMeta.handle) return window.pdpMeta.handle;
    var m = (location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }
  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }

  function garmentOf(p) {
    var b = String(p.bucket || '');
    if (b.indexOf('|leggings') >= 0) return 'leggings';
    if (b.indexOf('|shorts') >= 0) return 'shorts';
    return null;                                          // tops etc: no configurator
  }

  // Family member view over a catalog product (locked normalisation rules)
  function memberOf(p, garment) {
    var len;
    if (garment === 'shorts') {
      var raw = norm(p.length);
      len = raw === 'long shorts' ? 'long shorts' : 'shorts';   // bike shorts = shorts
    } else {
      len = norm(p.length) || 'full length';                    // missing length = full length
    }
    return {
      handle: p.handle,
      length: len,
      feature: norm(p.feature) === 'with pockets' ? 'with pockets' : 'no pockets',
      colour: norm(p.color) || 'black',                         // missing colour = black
      p: p
    };
  }

  // Range key: coreName minus its trailing garment word — "Maternity Leggings"
  // and "Maternity Shorts" both key to "maternity", so the LENGTH row spans the
  // whole bottoms spectrum of the range. The grid picker's separate
  // shorts/leggings entries are untouched; this only widens the option rows.
  function rangeKey(coreName) {
    return norm(coreName).replace(/\s+(leggings|shorts)$/, '');
  }

  function familyFor(handle) {
    var cat = catalog();
    if (!cat || !cat.products) return null;
    var cur = null;
    for (var i = 0; i < cat.products.length; i++) {
      if (cat.products[i].handle === handle) { cur = cat.products[i]; break; }
    }
    if (!cur) return null;
    if (!garmentOf(cur)) return null;                     // bottoms only; tops never configure
    var range = rangeKey(cur.coreName);
    if (!range) return null;
    var members = [], seen = {};
    cat.products.forEach(function (p) {
      var g = garmentOf(p);
      if (!g) return;                                     // bottoms only
      if (rangeKey(p.coreName) !== range) return;
      if (p.bundleEligible === false) return;             // drops never enter the configurator
      if (seen[p.handle]) return;
      seen[p.handle] = true;
      members.push(memberOf(p, g));
    });
    if (members.length < 2) return null;
    var me = null;
    members.forEach(function (m) { if (m.handle === handle) me = m; });
    if (!me) return null;
    return { range: range, members: members, current: me };
  }

  // Sellable-now check for a member at a size (catalog qty > 0)
  function inStockAt(member, size) {
    if (!size) return true;
    var vs = member.p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      if (String(vs[i].size || '').toUpperCase() === size && (vs[i].qty || 0) > 0) return true;
    }
    return false;
  }

  // The size SHE picked — trusted picks only. The auto-selected first size is
  // not a choice and must not compress the matrix.
  function herSize() {
    var p = window.__emUserSizePick;
    return (p && p.size) ? String(p.size).toUpperCase() : null;
  }

  function distinctValues(fam, axis) {
    var seen = {}, out = [];
    fam.members.forEach(function (m) {
      var v = m[axis.key];
      if (!seen[v]) { seen[v] = true; out.push(v); }
    });
    out.sort(function (a, b) {
      var ia = axis.order.indexOf(a), ib = axis.order.indexOf(b);
      if (ia < 0 && ib < 0) return a < b ? -1 : 1;
      if (ia < 0) return 1; if (ib < 0) return -1;
      return ia - ib;
    });
    return out;
  }

  function anyWithValueInSize(fam, axisKey, value, size) {
    for (var i = 0; i < fam.members.length; i++) {
      var m = fam.members[i];
      if (m[axisKey] === value && inStockAt(m, size)) return true;
    }
    return false;
  }

  // Best target for tapping (axisKey=value) — MINIMAL-CHANGE principle
  // (28 Jul, after UAT: tapping "no pockets" on 7/8 teleported to long
  // shorts). In-stock in her size dominates, then the LENGTH spectrum is a
  // distance metric — same length first, then neighbours, garment jumps
  // (leggings <-> shorts) only when nothing closer holds her size. Her other
  // choices (feature, colour) weigh in after that.
  var SPECTRUM = ['shorts', 'long shorts', '3/4', '7/8', 'full length'];
  function lenDist(a, b) {
    var ia = SPECTRUM.indexOf(a), ib = SPECTRUM.indexOf(b);
    if (ia < 0 || ib < 0) return 2;
    return Math.abs(ia - ib);
  }
  function garmentOfLen(l) { return (l === 'shorts' || l === 'long shorts') ? 'shorts' : 'leggings'; }
  function targetFor(fam, axisKey, value, size) {
    var best = null, bestScore = -Infinity;
    fam.members.forEach(function (m) {
      if (m[axisKey] !== value) return;
      var score = 0;
      if (size && inStockAt(m, size)) score += 1000;
      if (axisKey !== 'length') {
        score += 300 - 80 * lenDist(m.length, fam.current.length);
        if (garmentOfLen(m.length) === garmentOfLen(fam.current.length)) score += 150;
      }
      AXES.forEach(function (ax) {
        if (ax.key === axisKey || ax.key === 'length') return;
        if (m[ax.key] === fam.current[ax.key]) score += (ax.key === 'feature' ? 100 : 50);
      });
      var depth = 0;
      (m.p.variants || []).forEach(function (v) {
        if (!size || String(v.size || '').toUpperCase() === size) depth = Math.max(depth, v.qty || 0);
      });
      score += Math.min(depth, 50) / 100;
      if (score > bestScore) { bestScore = score; best = m; }
    });
    return best;
  }

  // Single-axis escapes from a dead current combo (for the rescue line + glow)
  function escapes(fam, size) {
    var out = [];
    AXES.forEach(function (ax) {
      distinctValues(fam, ax).forEach(function (v) {
        if (v === fam.current[ax.key]) return;
        // same other axes, this axis changed, her size sellable
        for (var i = 0; i < fam.members.length; i++) {
          var m = fam.members[i];
          if (m[ax.key] !== v) continue;
          var same = true;
          AXES.forEach(function (o) { if (o.key !== ax.key && m[o.key] !== fam.current[o.key]) same = false; });
          if (same && inStockAt(m, size)) { out.push({ axis: ax.key, value: v }); return; }
        }
      });
    });
    return out;
  }

  // ── P2c funnel events ─────────────────────────────────────────────────
  // Emits through the existing wardrobe pipeline; shown-events dedupe per
  // page/size/state so re-renders don't spam rows.
  var _emitted = {};
  function emit(type, props, dedupeKey) {
    try {
      if (dedupeKey) {
        if (_emitted[dedupeKey]) return;
        _emitted[dedupeKey] = true;
      }
      props = props || {};
      props.source_surface = 'family_configurator';
      if (typeof window.__scvEmit === 'function') window.__scvEmit(type, props);
    } catch (e) {}
  }

  // ── Ring 2: forward-stage rescue cards (agreed 28 Jul) ────────────────
  // Fires ONLY when the whole family is dead in her size. Last-resort
  // exception to the no-lifestage-jump rule: candidates come from the NEXT
  // stage(s), same garment, never backwards, outside the current range —
  // in stock in her exact size, deepest first, max three.
  var RING2_FORWARD = { maternity: ['postpartum', 'mum'], postpartum: ['mum'], mum: [] };
  var CUR_SYM = { AUD: '$', USD: '$', CAD: '$', NZD: '$', GBP: '£', EUR: '€', SGD: 'S$' };
  function curSym() {
    try { return CUR_SYM[(window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD'] || '$'; } catch (e) { return '$'; }
  }
  function stageOfProduct(p) { return String(p.bucket || '').split('|')[0]; }

  function ring2Candidates(fam, size) {
    var cat = catalog();
    if (!cat || !cat.products) return [];
    var stage = stageOfProduct(fam.current.p);
    var garment = garmentOf(fam.current.p);
    var stages = RING2_FORWARD[stage] || [];
    var out = [];
    stages.forEach(function (st) {
      cat.products.forEach(function (p) {
        if (p.bundleEligible === false) return;
        if (garmentOf(p) !== garment) return;
        if (String(p.bucket || '').indexOf(st + '|') !== 0) return;
        if (rangeKey(p.coreName) === fam.range) return;   // outside the dead family
        var best = null;
        (p.variants || []).forEach(function (v) {
          if (String(v.size || '').toUpperCase() !== size) return;
          if (!(typeof v.qty === 'number' && v.qty > 0)) return;
          if (!best || v.qty > best.qty) best = v;
        });
        if (best) out.push({ handle: p.handle, vid: best.id, qty: best.qty, price: best.price,
          name: p.simpleName || p.coreName || p.title, tagline: p.tagline || '', image: p.image || '' });
      });
    });
    out.sort(function (a, b) { return (b.qty || 0) - (a.qty || 0); });
    // dedupe by handle, cap 3
    var seen = {}, top = [];
    out.forEach(function (c) { if (!seen[c.handle] && top.length < 3) { seen[c.handle] = true; top.push(c); } });
    return top;
  }

  function renderRing2(fam, size, familyDead) {
    var old = document.getElementById('em-family-ring2');
    if (!familyDead || !size) { if (old) old.remove(); return; }
    var cards = ring2Candidates(fam, size);
    if (!cards.length) { if (old) old.remove(); return; }
    var atc = document.getElementById('add-to-cart-button');
    if (!atc) { if (old) old.remove(); return; }
    var host = old;
    if (!host) {
      host = document.createElement('div');
      host.id = 'em-family-ring2';
      // directly under the (disabled) ATC, per the approved S4 drawing
      atc.parentNode.insertBefore(host, atc.nextSibling);
    }
    var sym = curSym();
    var h = '<div class="fr2-cap">' + size + ' is ready to ship in these</div><div class="fr2-row">';
    cards.forEach(function (c) {
      h += '<button type="button" class="fr2-card" data-handle="' + c.handle + '">'
        + (c.image ? '<img src="' + c.image + '" alt="" loading="lazy">' : '<span class="fr2-ph"></span>')
        + '<span class="fr2-nm">' + c.name + '</span>'
        + '<span class="fr2-pr">' + sym + Math.ceil(c.price || 0) + (c.tagline ? ' · ' + String(c.tagline).slice(0, 26) : '') + '</span>'
        + '</button>';
    });
    h += '</div>';
    host.innerHTML = h;
    emit('fc_ring2_shown', { handle: fam.current.handle, size: size, cards: cards.map(function (c) { return c.handle; }).join(',') },
      'r2|' + fam.current.handle + '|' + size + '|' + cards.map(function (c) { return c.handle; }).join(','));
    host.querySelectorAll('.fr2-card').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        var to = btn.getAttribute('data-handle');
        emit('fc_ring2_tap', { from_handle: fam.current.handle, to_handle: to, size: size });
        if (typeof window._v2SwapToHandle === 'function') window._v2SwapToHandle(to, { preserveScroll: true });
        else location.href = '/products/' + to;
      });
    });
  }

  function ensureStyles() {
    if (document.getElementById('em-fc-style')) return;
    var css = ''
      + '#' + HOST_ID + '{margin:2px 0 0;font-family:system-ui,-apple-system,sans-serif;}'
      + '#' + HOST_ID + ' .fc-lbl{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:#6b7280;margin:12px 0 5px;}'
      + '#' + HOST_ID + ' .fc-row{display:flex;gap:7px;flex-wrap:wrap;align-items:center;}'
      + '#' + HOST_ID + ' .fc-opt{font-size:13px;border:1px solid #d1d5db;border-radius:4px;padding:6px 13px;background:#fff;color:#1f2937;cursor:pointer;line-height:1.2;}'
      + '#' + HOST_ID + ' .fc-opt.sel{border-color:#1f2937;font-weight:700;}'
      + '#' + HOST_ID + ' .fc-opt.dead{text-decoration:line-through;opacity:.45;cursor:default;}'
      + '#' + HOST_ID + ' .fc-opt.go{border-color:#3D9A8B;color:#3D9A8B;font-weight:600;}'
      + '#' + HOST_ID + ' .fc-dot{width:22px;height:22px;border-radius:50%;border:1px solid #d1d5db;padding:0;cursor:pointer;position:relative;flex:none;}'
      + '#' + HOST_ID + ' .fc-dot.sel:after{content:"";position:absolute;inset:-5px;border:2px solid #1f2937;border-radius:50%;}'
      + '#' + HOST_ID + ' .fc-dot.go:after{content:"";position:absolute;inset:-5px;border:2px solid #3D9A8B;border-radius:50%;}'
      + '#' + HOST_ID + ' .fc-dot.dead{opacity:.35;cursor:default;}'
      + '#' + HOST_ID + ' .fc-rescue{margin:11px 0 0;font-size:12.5px;font-weight:600;color:#3D9A8B;}'
      + '#em-family-ring2{margin:12px 0 0;font-family:system-ui,-apple-system,sans-serif;}'
      + '#em-family-ring2 .fr2-cap{font-size:12px;font-weight:700;color:#3D9A8B;margin:0 0 7px;}'
      + '#em-family-ring2 .fr2-row{display:flex;gap:8px;}'
      + '#em-family-ring2 .fr2-card{flex:1;min-width:0;border:1px solid #3D9A8B;border-radius:6px;background:#fff;padding:6px;font-size:11px;line-height:1.35;cursor:pointer;text-align:left;display:flex;flex-direction:column;gap:3px;}'
      + '#em-family-ring2 .fr2-card img{width:100%;height:72px;object-fit:cover;border-radius:4px;display:block;}'
      + '#em-family-ring2 .fr2-ph{width:100%;height:72px;border-radius:4px;background:#f3f4f6;display:block;}'
      + '#em-family-ring2 .fr2-nm{font-weight:600;color:#1f2937;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}'
      + '#em-family-ring2 .fr2-pr{color:#6b7280;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}';
    var el = document.createElement('style');
    el.id = 'em-fc-style';
    el.textContent = css;
    document.head.appendChild(el);
  }

  function render() {
    var sizeRow = document.getElementById('size-buttons');
    var host = document.getElementById(HOST_ID);
    var handle = currentHandle();
    var fam = handle ? familyFor(handle) : null;
    if (!sizeRow || !fam) {
      if (host) host.remove();
      var r2gone = document.getElementById('em-family-ring2');
      if (r2gone) r2gone.remove();
      return;
    }

    ensureStyles();
    if (!host) {
      host = document.createElement('div');
      host.id = HOST_ID;
      sizeRow.parentNode.insertBefore(host, sizeRow.nextSibling);
    }
    host.innerHTML = '';

    var size = herSize();
    var currentDead = size ? !inStockAt(fam.current, size) : false;
    var esc = currentDead ? escapes(fam, size) : [];
    var escKey = {};
    esc.forEach(function (e) { escKey[e.axis + '|' + e.value] = true; });

    // Whole family dead in her size -> Ring 2 (forward-stage rescue cards).
    var familyDead = false;
    if (size && currentDead) {
      familyDead = true;
      for (var fi = 0; fi < fam.members.length; fi++) {
        if (inStockAt(fam.members[fi], size)) { familyDead = false; break; }
      }
    }
    renderRing2(fam, size, familyDead);
    if (currentDead && esc.length) {
      emit('fc_rescue_shown',
        { handle: fam.current.handle, size: size, escapes: esc.map(function (e) { return e.axis + ':' + e.value; }).join(',') },
        'rs|' + fam.current.handle + '|' + size);
    }

    AXES.forEach(function (ax) {
      var values = distinctValues(fam, ax);
      if (values.length < 2) return;                      // no structural choice — row absent
      var shown = [];
      values.forEach(function (v) {
        var isCurrent = v === fam.current[ax.key];
        var avail = !size || anyWithValueInSize(fam, ax.key, v, size);
        if (!avail && !isCurrent) return;                  // D1: hidden, not struck
        shown.push({ v: v, isCurrent: isCurrent, avail: avail });
      });
      if (!shown.length) return;
      if (shown.length === 1 && shown[0].isCurrent && shown[0].avail && values.length < 3) {
        // lone healthy current chip carries no information — skip the row
        return;
      }
      var lbl = document.createElement('div');
      lbl.className = 'fc-lbl';
      lbl.textContent = ax.label;
      host.appendChild(lbl);
      var row = document.createElement('div');
      row.className = 'fc-row';
      shown.forEach(function (o) {
        var b = document.createElement('button');
        b.type = 'button';
        var isDot = ax.key === 'colour' && SWATCH[o.v];
        b.className = isDot ? 'fc-dot' : 'fc-opt';
        if (isDot) { b.style.background = SWATCH[o.v]; b.setAttribute('aria-label', o.v); b.title = o.v; }
        else b.textContent = o.v;
        if (o.isCurrent) b.classList.add(o.avail ? 'sel' : 'sel');
        if (o.isCurrent && !o.avail) b.classList.add('dead');
        if (escKey[ax.key + '|' + o.v]) b.classList.add('go');
        b.addEventListener('click', function (ev) {
          ev.preventDefault();
          if (o.isCurrent) return;
          var t = targetFor(fam, ax.key, o.v, size);
          if (!t || t.handle === fam.current.handle) return;
          var jumped = ax.key !== 'length' && t.length !== fam.current.length;
          emit('fc_option_tap', {
            axis: ax.key, value: o.v, from_handle: fam.current.handle, to_handle: t.handle,
            size: size || '', length_jump: jumped ? 'yes' : 'no', was_rescue: escKey[ax.key + '|' + o.v] ? 'yes' : 'no'
          });
          // Transparency for unavoidable length jumps: when a non-length tap
          // has to land on a different length (nothing closer holds her
          // size), say so once on the next render instead of moving silently.
          if (jumped) {
            window.__emFcJump = { value: o.v, toLength: t.length, at: Date.now ? Date.now() : 0 };
          }
          // preserveScroll: the shopper is standing at the option rows — the
          // swap happens under her thumb with the existing loading veil; the
          // page must not move (mobile taps were jumping to the top).
          if (typeof window._v2SwapToHandle === 'function') window._v2SwapToHandle(t.handle, { preserveScroll: true });
          else location.href = '/products/' + t.handle;
        });
        row.appendChild(b);
      });
      host.appendChild(row);
    });

    if (currentDead && esc.length) {
      var labels = [];
      esc.forEach(function (e) { if (labels.length < 2 && labels.indexOf(e.value) < 0) labels.push(e.value); });
      var line = document.createElement('div');
      line.className = 'fc-rescue';
      line.textContent = 'Size ' + size + ' is ready to ship in ' + labels.join(' or ');
      host.appendChild(line);
    } else if (window.__emFcJump && window.__emFcJump.toLength === fam.current.length) {
      // One-shot note after an unavoidable length jump (rescue tone, D2).
      var j = window.__emFcJump;
      window.__emFcJump = null;
      var jl = document.createElement('div');
      jl.className = 'fc-rescue';
      jl.textContent = j.value + (size ? ' in ' + size : '') + ' ships in ' + j.toLength + ' — switched for you';
      host.appendChild(jl);
    }
  }

  // Re-render whenever the world changes underneath us.
  var t = null;
  function schedule() { clearTimeout(t); t = setTimeout(render, 60); }
  window.addEventListener('em:size:selected', schedule);
  window.addEventListener('em:v2:swapped', schedule);
  window.addEventListener('popstate', schedule);
  function boot() {
    var sr = document.getElementById('size-buttons');
    if (sr) new MutationObserver(schedule).observe(sr, { childList: true });
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
